import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Bus, MapPin, Search, Clock, CheckCircle, 
  AlertCircle, Navigation, Users, Phone
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TrackingPage = () => {
  const [searchParams] = useSearchParams();
  const initialRef = searchParams.get('ref') || '';
  
  const [bookingRef, setBookingRef] = useState(initialRef);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [gpsLocation, setGpsLocation] = useState(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (initialRef) {
      handleTrack();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const handleTrack = async () => {
    if (!bookingRef.trim()) {
      toast.error('Please enter a booking reference');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/track/${bookingRef}`);
      setTracking(response.data);
      
      // Connect to GPS WebSocket
      connectToGPS(response.data.trip?.trip_id);
    } catch (error) {
      console.error('Tracking failed:', error);
      toast.error('Booking not found. Please check your reference number.');
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  const connectToGPS = (tripId) => {
    if (!tripId) return;
    
    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${wsProto}//${window.location.host}/ws/gps/${tripId}`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onmessage = (event) => {
      const location = JSON.parse(event.data);
      setGpsLocation(location);
    };

    wsRef.current.onerror = (error) => {
      console.log('WebSocket error:', error);
    };
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      cancelled: { color: 'bg-red-100 text-red-700', icon: AlertCircle },
      scheduled: { color: 'bg-blue-100 text-blue-700', icon: Clock },
      'in_transit': { color: 'bg-purple-100 text-purple-700', icon: Navigation },
      completed: { color: 'bg-slate-100 text-slate-700', icon: CheckCircle },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  return (
    <PublicLayout>
      <div className="bg-panashe-blue py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-panashe-gold rounded-full flex items-center justify-center mx-auto mb-4">
            <Navigation className="h-8 w-8 text-slate-900" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase mb-2">
            Track Your Bus
          </h1>
          <p className="text-white/80">
            Enter your booking reference to track your bus in real-time
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-8">
        {/* Search Card */}
        <Card className="card-panashe shadow-xl">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <Input
                placeholder="Enter booking reference (e.g., PAN241201ABC123)"
                value={bookingRef}
                onChange={(e) => setBookingRef(e.target.value.toUpperCase())}
                className="font-mono"
                onKeyDown={(e) => e.key === 'Enter' && handleTrack()}
                data-testid="tracking-input"
              />
              <Button 
                className="btn-panashe"
                onClick={handleTrack}
                disabled={loading}
                data-testid="track-btn"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Track
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tracking Results */}
        {tracking && (
          <div className="mt-8 space-y-6 animate-slide-up">
            {/* Status Card */}
            <Card className="card-panashe">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Bus className="h-5 w-5" />
                    Booking Status
                  </CardTitle>
                  {getStatusBadge(tracking.status)}
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Reference</p>
                    <p className="font-mono font-bold">{tracking.booking_ref}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Check-in</p>
                    <p className="font-medium">
                      {tracking.checked_in ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-4 w-4" />
                          Checked In
                        </span>
                      ) : (
                        <span className="text-slate-500">Not Yet</span>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trip Info */}
            <Card className="card-panashe">
              <CardHeader className="border-b">
                <CardTitle className="font-heading flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Trip Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold text-slate-900">
                      {tracking.route?.origin}
                    </p>
                    <p className="text-sm text-slate-500">Departure</p>
                    {tracking.trip?.departure && (
                      <p className="text-panashe-blue font-medium">
                        {format(parseISO(tracking.trip.departure), 'HH:mm')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex-1 mx-4">
                    <div className="flex items-center">
                      <div className="h-3 w-3 rounded-full bg-panashe-blue" />
                      <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                      <Bus className="h-6 w-6 text-panashe-gold mx-2" />
                      <div className="flex-1 border-t-2 border-dashed border-slate-300" />
                      <div className="h-3 w-3 rounded-full bg-panashe-gold" />
                    </div>
                    <p className="text-center text-xs text-slate-500 mt-1">
                      {tracking.route?.name}
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <p className="font-heading text-2xl font-bold text-slate-900">
                      {tracking.route?.destination}
                    </p>
                    <p className="text-sm text-slate-500">Arrival</p>
                    {tracking.trip?.arrival && (
                      <p className="text-panashe-gold font-medium">
                        {format(parseISO(tracking.trip.arrival), 'HH:mm')}
                      </p>
                    )}
                  </div>
                </div>

                {/* Trip Status */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Trip Status</span>
                    {getStatusBadge(tracking.trip?.status)}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* GPS Location */}
            {gpsLocation && (
              <Card className="card-panashe">
                <CardHeader className="border-b">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Navigation className="h-5 w-5 text-green-600" />
                    Live Location
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="bg-slate-100 rounded-lg p-8 flex flex-col items-center justify-center">
                    <Bus className="h-16 w-16 text-panashe-blue mb-4 animate-pulse" />
                    <p className="text-sm text-slate-600 mb-2">Bus is currently at:</p>
                    <p className="font-mono text-lg">
                      {gpsLocation.lat?.toFixed(4)}°S, {gpsLocation.lng?.toFixed(4)}°E
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      Speed: {gpsLocation.speed} km/h
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Updated: {gpsLocation.timestamp && format(parseISO(gpsLocation.timestamp), 'HH:mm:ss')}
                    </p>
                  </div>
                  <p className="text-xs text-slate-500 text-center mt-4">
                    * GPS tracking is simulated for demonstration
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Passengers */}
            <Card className="card-panashe">
              <CardHeader className="border-b">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Passengers
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  {tracking.passengers?.map((passenger, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 bg-panashe-blue rounded-full flex items-center justify-center text-white font-bold">
                        {passenger.full_name?.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{passenger.full_name}</p>
                        <p className="text-sm text-slate-500">Seat {tracking.seats?.[idx]}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Contact Support */}
            <Card className="card-panashe bg-panashe-blue text-white">
              <CardContent className="p-6 text-center">
                <h3 className="font-heading text-lg font-bold mb-2">Need Help?</h3>
                <p className="text-white/80 mb-4">Contact our 24/7 support line</p>
                <a href="tel:+263771234567">
                  <Button variant="secondary" className="bg-panashe-gold text-slate-900 hover:bg-panashe-gold/90">
                    <Phone className="h-4 w-4 mr-2" />
                    +263 77 123 4567
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State */}
        {!tracking && !loading && (
          <div className="mt-12 text-center text-slate-500">
            <Bus className="h-24 w-24 mx-auto text-slate-200 mb-4" />
            <p>Enter your booking reference to track your bus</p>
          </div>
        )}
      </div>
    </PublicLayout>
  );
};

export default TrackingPage;
